import {
  Button,
  Chip,
  FormControl,
  Grid,
  Input,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Typography,
} from '@material-ui/core';
import { useTheme, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import isEqual from 'lodash.isequal';
import React, { useState, useEffect, useContext } from 'react';

import { ctx, IStore } from 'store';

import { useStyles } from './styles';

const Notifications: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const actions = useContext(ctx.actions);

  const {
    teams: { currentTeam },
  } = useContext(ctx.store);

  const [personName, setPersonName] = useState<string[]>([]);
  const [sensibility, setSensibility] = useState<number>(currentTeam.alertsSensibility);

  useEffect(() => {
    if (currentTeam && currentTeam.supervisors && currentTeam.supervisors.length > 0) {
      const supervisors = getSupervisorsUsernames();
      setPersonName(supervisors);
    }
  }, [currentTeam.supervisors]);

  function getStyles(name: string, personName: string[], theme: Theme) {
    return {
      fontWeight:
        personName.indexOf(name) === -1
          ? theme.typography.fontWeightRegular
          : theme.typography.fontWeightMedium,
    };
  }

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const getSupervisorsUsernames = () => {
    if (currentTeam && currentTeam.supervisors && currentTeam.supervisors.length > 0) {
      const teamVar = currentTeam.teamId ? currentTeam.members : currentTeam.msMembers;
      return teamVar
        .map((member: any) => {
          if (currentTeam.supervisors.map((item: any) => item.memberId).includes(member.memberId)) {
            return member.profile.displayName
              ? member.profile.displayName
              : member.profile.realName;
          }
        })
        .filter((memberArray: any) => memberArray);
    }

    return [];
  };

  const handleSubmit = async (type: string) => {
    await ((actions.misc.setLoading(true) as unknown) as Promise<IStore>);
    if (currentTeam.teamId) {
      if (type === 'supervisors') {
        const supervisors = currentTeam.members
          .map((member: any) => {
            const item = { memberId: member.memberId, email: member.profile.email };
            if (personName.includes(member.profile.displayName)) {
              return item;
            }
          })
          .filter((memberArray: any) => memberArray);
        actions.teams.updateTeam('supervisors', supervisors);
      } else {
        actions.teams.updateTeam('alertsSensibility', sensibility);
      }
    } else {
      if (type === 'supervisors') {
        const supervisors = currentTeam.msMembers
          // eslint-disable-next-line sonarjs/no-identical-functions
          .map((member: any) => {
            const item = { memberId: member.memberId, email: member.profile.email };
            if (personName.includes(member.profile.displayName)) {
              return item;
            }
          })
          .filter((memberArray: any) => memberArray);
        actions.tenants.updateTenant('supervisors', supervisors);
      } else {
        actions.tenants.updateTenant('alertsSensibility', sensibility);
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setPersonName(event.target.value as string[]);
  };

  const supervisorsChanged = !isEqual(personName, getSupervisorsUsernames());

  const handleSensibility = (_event: any, newValue: any) => {
    setSensibility(newValue);
  };

  const sensibilityText = () => {
    switch (sensibility) {
      case 1:
        return 'Low';
      case 2:
        return 'Medium';
      case 3:
        return 'High';
      default:
        return 'Medium';
    }
  };

  return (
    <Grid container>
      <Grid item xs={12} className={classes.metricGridItems}>
        <Paper className={clsx(classes.paper, classes.metricBoxChart)}>
          <Grid className={classes.sides}>
            <Typography component="h2" className={classes.subtitle}>
              Administrators
            </Typography>
            <Typography component="h2" className={classes.text}>
              Administrators will receive notifications and emails related to insights, incidents,
              and more.
            </Typography>
          </Grid>
          <Grid className={classes.sides}>
            <FormControl className={classes.formControl}>
              <InputLabel id="demo-mutiple-chip-label">Administrators</InputLabel>
              <Select
                className={classes.supInput}
                labelId="demo-mutiple-chip-label"
                id="demo-mutiple-chip"
                multiple
                value={personName}
                onChange={handleChange}
                input={<Input id="select-multiple-chip" />}
                renderValue={selected => (
                  <div className={classes.chips}>
                    {(selected as string[]).map(value => (
                      <Chip key={value} label={value} className={classes.chip} />
                    ))}
                  </div>
                )}
                MenuProps={MenuProps}
              >
                {(currentTeam.teamId ? currentTeam.members : currentTeam.msMembers).map(
                  (member: any) => (
                    <MenuItem
                      key={
                        member.profile.displayName
                          ? member.profile.displayName
                          : member.profile.realName
                      }
                      value={
                        member.profile.displayName
                          ? member.profile.displayName
                          : member.profile.realName
                      }
                      style={getStyles(member.profile.displayName, personName, theme)}
                    >
                      {member.profile.displayName
                        ? member.profile.displayName
                        : member.profile.realName}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={!supervisorsChanged}
              className={classes.button}
              onClick={() => handleSubmit('supervisors')}
            >
              Save
            </Button>
          </Grid>
        </Paper>
      </Grid>
      {currentTeam.teamId && (
        <Grid item xs={12} className={classes.metricGridItems}>
          <Paper className={clsx(classes.paper, classes.metricBoxChart)}>
            <Grid className={classes.sides}>
              <Typography component="h2" className={classes.subtitle}>
                Alerts
              </Typography>
              <Typography component="h2" className={classes.text}>
                Alert sensitivity modifies the frequency of notifications when positive or negative
                events occur in your workspace
              </Typography>
            </Grid>
            <Grid className={classes.sides}>
              <FormControl className={classes.formControl}>
                <Slider
                  onChange={handleSensibility}
                  value={sensibility}
                  getAriaValueText={sensibilityText}
                  aria-labelledby="discrete-slider"
                  step={1}
                  marks
                  min={1}
                  max={3}
                />
                <Typography component="h2" className={classes.text}>
                  {`${sensibilityText()} sensibility for your alerts`}
                </Typography>
              </FormControl>
              <Button
                disabled={sensibility === currentTeam.alertsSensibility}
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={() => handleSubmit('alertsSensibility')}
              >
                Save
              </Button>
            </Grid>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export { Notifications };
